import {
  effectiveFieldPresets,
  variableTypeMappingSchema,
  type FieldPreset,
} from "./field-preset-catalog";
import { type Field, type Project } from "./schema";

/** Match words rather than substrings: shippingPhone matches, microphone does not. */
export function normalizedVariableName(value: string) {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .replace(/\s+/g, " ");
}
export function matchVariableType(
  field: Pick<Field, "key" | "label">,
  rows: FieldPreset[],
) {
  const names = [field.key, field.label].map(normalizedVariableName);
  const matches = effectiveFieldPresets(
    rows.filter((p) => p.kind === "variable-type"),
  ).flatMap((p) => {
    const value = variableTypeMappingSchema.parse(p.value);
    if (!value.enabled) return [];
    const score = Math.max(
      0,
      ...value.aliases.map((alias) => {
        const word = normalizedVariableName(alias);
        if (!word) return 0;
        return names.some((name) => name === word)
          ? 10000 + word.length
          : value.match === "words" &&
              names.some((name) => ` ${name} `.includes(` ${word} `))
            ? word.length
            : 0;
      }),
    );
    return score ? [{ preset: p, value, score }] : [];
  });
  return matches.sort(
    (a, b) => b.score - a.score || a.preset.key.localeCompare(b.preset.key),
  )[0];
}
export function mapVariableType(field: Field, rows: FieldPreset[]): Field {
  // Explicit types, source calculations, choices and policies are authoritative.
  const legacy = field.legacy?.compositionVariable as
    Record<string, unknown> | undefined;
  if (
    field.type !== "text" ||
    field.calculation ||
    field.choices.length ||
    field.system ||
    legacy?.rules ||
    legacy?.script ||
    legacy?.kind === "Rule" ||
    !["literal", "form"].includes(field.source.kind) ||
    Object.keys(field.validation).some((key) => key !== "rule") ||
    (field.validation.rule && field.validation.rule !== "auto") ||
    field.format.kind
  )
    return field;
  const match = matchVariableType(field, rows);
  return match ? { ...field, type: match.value.dataType } : field;
}
export function previewVariableTypes(project: Project, rows: FieldPreset[]) {
  return project.fields.flatMap((field) => {
    const mapped = mapVariableType(field, rows);
    if (mapped.type === field.type) return [];
    const match = matchVariableType(field, rows)!;
    return [
      {
        fieldId: field.id,
        label: field.label,
        from: field.type,
        to: mapped.type,
        mapping: match.preset.name,
        scope: match.preset.scope.kind,
      },
    ];
  });
}
export function applyVariableTypes(
  project: Project,
  rows: FieldPreset[],
): Project {
  return {
    ...project,
    fields: project.fields.map((field) => mapVariableType(field, rows)),
  };
}
